import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, money } from "../lib/api";
import { fbTrack } from "../lib/fbpixel";
import { Check, Copy, Loader2, Bitcoin, Coins, Send, Truck, RefreshCw, AtSign, MessageCircle } from "lucide-react";

export default function OrderConfirmation() {
  const { orderNumber } = useParams();
  const [order, setOrder] = useState(null);
  const [state, setState] = useState("loading");
  const [copied, setCopied] = useState("");

  useEffect(() => {
    api
      .get(`/orders/${orderNumber}`)
      .then((r) => {
        setOrder(r.data);
        setState("ok");
        const o = r.data;
        const fbKey = `cl_fb_purchase_${o.order_number}`;
        if (!sessionStorage.getItem(fbKey)) {
          fbTrack("Purchase", {
            content_ids: (o.items || []).map((it) => it.slug || it.product_id || it.name),
            content_type: "product",
            num_items: (o.items || []).reduce((s, it) => s + (it.quantity || 0), 0),
            value: +Number(o.total || 0).toFixed(2),
            currency: "USD",
          });
          sessionStorage.setItem(fbKey, "1");
        }
      })
      .catch(() => setState("error"));
  }, [orderNumber]);

  // Auto-poll for BTCPay settlement so the page updates the moment payment confirms
  useEffect(() => {
    if (!order || order.payment_method !== "bitcoin" || !order.payment?.btcpay) return;
    if (order.status === "paid") return;
    const iv = setInterval(() => {
      api.get(`/orders/${orderNumber}`).then((r) => setOrder(r.data)).catch(() => {});
    }, 5000);
    return () => clearInterval(iv);
  }, [orderNumber, order?.payment_method, order?.payment?.btcpay, order?.status]);

  const copy = (text, key) => {
    if (navigator.clipboard) navigator.clipboard.writeText(text);
    setCopied(key);
    setTimeout(() => setCopied(""), 1500);
  };

  if (state === "loading") {
    return (
      <div className="min-h-[70vh] bg-[#070B1C] flex items-center justify-center">
        <Loader2 size={36} className="animate-spin text-electric" />
      </div>
    );
  }
  if (state === "error" || !order) {
    return (
      <div className="min-h-[70vh] bg-[#070B1C] flex items-center justify-center px-5 py-20 text-center">
        <div data-testid="order-not-found">
          <p className="text-sm tracking-[0.2em] uppercase text-white/60 mb-6">Order not found</p>
          <Link to="/" className="btn-primary inline-block px-9 py-4 text-xs tracking-[0.2em] uppercase font-bold">Back to Shop</Link>
        </div>
      </div>
    );
  }

  const p = order.payment;
  const isBtc = order.payment_method === "bitcoin";
  const isCrypto = order.payment_method === "crypto";
  const isVenmo = order.payment_method === "venmo";
  const isOther = order.payment_method === "other";
  const payLabel = isBtc ? "Bitcoin" : isCrypto ? "Crypto" : isVenmo ? "Venmo" : "Zelle";
  const paid = order.status === "paid";
  const custName = `${order.shipping?.first_name || ""} ${order.shipping?.last_name || ""}`.trim();

  return (
    <div className="bg-[#070B1C] min-h-screen py-10 lg:py-16 px-5">
      <div className="max-w-2xl mx-auto" data-testid="order-confirmation">
        {/* Header */}
        <div className="text-center mb-10">
          <div className="w-16 h-16 border border-electric mx-auto flex items-center justify-center text-electric mb-6 rounded-full">
            <Check size={32} strokeWidth={2} />
          </div>
          <h1 className="font-display font-bold text-3xl sm:text-4xl tracking-tighter uppercase text-white">{paid ? "Payment Confirmed" : "Order Received"}</h1>
          <p className="text-sm text-white/60 mt-3">
            Order <span className="text-electric font-bold" data-testid="order-number">{order.order_number}</span> · {paid ? "Paid" : "Awaiting payment"}
          </p>
          <p className="text-xs text-white/40 mt-2">A copy of these instructions was emailed to {order.email}.</p>
        </div>

        {/* Payment instructions */}
        <div className="border border-electric/40 bg-[#0B1024] p-6 lg:p-8 mb-6 sq-lg" data-testid="payment-instructions">
          <div className="flex items-center gap-2 mb-5">
            {isBtc ? <Bitcoin size={18} className="text-electric" /> : isCrypto ? <Coins size={18} className="text-electric" /> : isVenmo ? <AtSign size={18} className="text-electric" /> : isOther ? <MessageCircle size={18} className="text-electric" /> : <Send size={18} className="text-electric" />}
            <span className="text-[10px] tracking-[0.25em] uppercase font-bold text-electric">
              {isOther ? "Payment to arrange" : isCrypto ? `Pay ${money(order.total)} in crypto` : `Pay ${money(order.total)} via ${payLabel}`}
            </span>
          </div>

          {isBtc && p.btcpay ? (
            <div className="space-y-4">
              {paid ? (
                <div className="flex items-center gap-2 border border-emerald-400/40 bg-emerald-400/10 px-4 py-4 sq-md text-emerald-300" data-testid="btcpay-paid">
                  <Check size={18} />
                  <span className="text-sm font-bold">Payment received, your order is confirmed and will ship shortly.</span>
                </div>
              ) : (
                <>
                  <a href={p.checkout_link} target="_blank" rel="noreferrer" className="btn-primary w-full inline-flex items-center justify-center gap-2 py-4 text-xs tracking-[0.2em] uppercase font-bold" data-testid="btcpay-checkout-link">
                    <Bitcoin size={16} /> Pay {money(order.total)} with Bitcoin
                  </a>
                  <div className="flex items-center gap-2 text-[11px] text-white/50" data-testid="btcpay-status">
                    <Loader2 size={13} className="animate-spin text-electric" /> Waiting for payment to confirm, this page updates automatically.
                  </div>
                  <p className="text-[11px] text-white/40 leading-relaxed">
                    Opens our secure Bitcoin checkout. The invoice locks in the exact BTC amount and confirms automatically once the network settles it. Order <b className="text-white">{order.order_number}</b> ships once payment clears.
                  </p>
                </>
              )}
            </div>
          ) : isBtc ? (
            <div>
              {p.qr && (
                <div className="flex justify-center mb-5">
                  <img src={p.qr} alt="Bitcoin payment QR" className="w-44 h-44 border border-white/15 bg-white p-2 sq-md" data-testid="btc-qr" />
                </div>
              )}
              <div className="space-y-4">
                <div>
                  <div className="text-[9px] tracking-[0.2em] uppercase text-white/40 mb-1">Amount (BTC)</div>
                  <div className="flex items-center justify-between gap-3 border border-white/15 bg-[#0C1128] px-4 py-3 sq-sm">
                    <span className="font-display font-bold text-lg text-white break-all" data-testid="btc-amount">{p.btc_amount ?? ", "} BTC</span>
                    {p.btc_amount != null && (
                      <button onClick={() => copy(String(p.btc_amount), "amt")} className="inline-flex items-center gap-1 text-[10px] tracking-[0.15em] uppercase font-bold text-electric hover:text-white shrink-0" data-testid="copy-btc-amount">
                        {copied === "amt" ? <Check size={13} /> : <Copy size={13} />} {copied === "amt" ? "Copied" : "Copy"}
                      </button>
                    )}
                  </div>
                  {p.btc_price_usd && <p className="text-[10px] text-white/35 mt-1">≈ {money(order.total)} at ${p.btc_price_usd.toLocaleString()} / BTC</p>}
                </div>
                <div>
                  <div className="text-[9px] tracking-[0.2em] uppercase text-white/40 mb-1">Bitcoin Address</div>
                  <div className="flex items-center justify-between gap-3 border border-white/15 bg-[#0C1128] px-4 py-3 sq-sm">
                    <span className="text-xs text-white break-all" data-testid="btc-address">{p.btc_address}</span>
                    <button onClick={() => copy(p.btc_address, "addr")} className="inline-flex items-center gap-1 text-[10px] tracking-[0.15em] uppercase font-bold text-electric hover:text-white shrink-0" data-testid="copy-btc-address">
                      {copied === "addr" ? <Check size={13} /> : <Copy size={13} />} {copied === "addr" ? "Copied" : "Copy"}
                    </button>
                  </div>
                </div>
                {p.uri && (
                  <a href={p.uri} className="btn-ghost w-full inline-flex items-center justify-center py-3 text-[10px] tracking-[0.2em] uppercase font-bold" data-testid="btc-open-wallet">
                    Open in Wallet
                  </a>
                )}
              </div>
            </div>
          ) : isCrypto ? (
            <div className="space-y-4" data-testid="crypto-payment">
              <p className="text-sm text-white/70 leading-relaxed">
                Pay with <b className="text-white">Solana, Bitcoin, or XRP</b>. Message us with your
                preferred coin and we'll send you the wallet address to complete your{" "}
                <b className="text-white">{money(order.total)}</b> payment.
              </p>
              <div className="flex flex-wrap gap-2" data-testid="crypto-coins">
                {["Solana", "Bitcoin", "XRP"].map((coin) => (
                  <span key={coin} className="inline-flex items-center gap-1.5 border border-electric/40 bg-electric/10 text-electric px-3 py-1.5 text-[10px] tracking-[0.15em] uppercase font-bold sq-sm">
                    <Coins size={12} /> {coin}
                  </span>
                ))}
              </div>
              <a
                href={`mailto:${p.contact_email}?subject=${encodeURIComponent(`Crypto payment, Order ${order.order_number}, ${custName}`)}&body=${encodeURIComponent(`Hi, I'd like to pay for order ${order.order_number} (${custName}) in crypto. My preferred coin is (Solana / Bitcoin / XRP): `)}`}
                className="btn-primary w-full inline-flex items-center justify-center gap-2 py-4 text-xs tracking-[0.2em] uppercase font-bold"
                data-testid="crypto-message-btn"
              >
                <MessageCircle size={16} /> Message Us for a Crypto Address
              </a>
              <div className="space-y-2 text-[11px] text-white/50">
                <div>Email: <a className="text-electric" href={`mailto:${p.contact_email}`} data-testid="crypto-contact-email">{p.contact_email}</a></div>
                {p.contact_phone && <div>Text: <a className="text-electric" href={`sms:${p.contact_phone}`} data-testid="crypto-contact-phone">{p.contact_phone}</a></div>}
              </div>
              <p className="text-[11px] text-white/40 leading-relaxed">
                Include order <b className="text-white">{order.order_number}</b> and your name <b className="text-white">{custName}</b> so we can match and fulfill it.
              </p>
            </div>
          ) : isOther ? (
            <div className="space-y-4" data-testid="contact-payment">
              <p className="text-sm text-white/70 leading-relaxed">
                No problem, we'll help you pay another way (card, Cash App, and more). Message us and we'll send payment details.
              </p>
              <a
                href={`mailto:${p.contact_email}?subject=${encodeURIComponent(`Order ${order.order_number}, ${custName}`)}&body=${encodeURIComponent(`Hi, I'd like to arrange payment for order ${order.order_number} (${custName}). My preferred method is: `)}`}
                className="btn-primary w-full inline-flex items-center justify-center gap-2 py-4 text-xs tracking-[0.2em] uppercase font-bold"
                data-testid="contact-email-btn"
              >
                <MessageCircle size={16} /> Message Us to Pay
              </a>
              <div className="space-y-2 text-[11px] text-white/50">
                <div>Email: <a className="text-electric" href={`mailto:${p.contact_email}`} data-testid="contact-email">{p.contact_email}</a></div>
                {p.contact_phone && <div>Text: <a className="text-electric" href={`sms:${p.contact_phone}`} data-testid="contact-phone">{p.contact_phone}</a></div>}
              </div>
              <p className="text-[11px] text-white/40 leading-relaxed">
                Include order <b className="text-white">{order.order_number}</b> and your name <b className="text-white">{custName}</b> so we can match and fulfill it.
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              <div>
                <div className="text-[9px] tracking-[0.2em] uppercase text-white/40 mb-1">Send {payLabel} to</div>
                <div className="flex items-center justify-between gap-3 border border-white/15 bg-[#0C1128] px-4 py-3 sq-sm">
                  <span className="font-display font-bold text-xl text-white" data-testid={isVenmo ? "venmo-handle" : "zelle-phone"}>{isVenmo ? p.venmo_handle : p.zelle_phone}</span>
                  <button onClick={() => copy(isVenmo ? p.venmo_handle : p.zelle_phone, "z")} className="inline-flex items-center gap-1 text-[10px] tracking-[0.15em] uppercase font-bold text-electric hover:text-white shrink-0" data-testid={isVenmo ? "copy-venmo" : "copy-zelle"}>
                    {copied === "z" ? <Check size={13} /> : <Copy size={13} />} {copied === "z" ? "Copied" : "Copy"}
                  </button>
                </div>
              </div>
              <div>
                <div className="text-[9px] tracking-[0.2em] uppercase text-white/40 mb-1">Recipient</div>
                <div className="border border-white/15 bg-[#0C1128] px-4 py-3 text-sm text-white sq-sm">{isVenmo ? p.venmo_name : p.zelle_name}</div>
              </div>
              <p className="text-[11px] text-white/50 leading-relaxed">
                {isVenmo ? (
                  <>In the Venmo note add order <b className="text-white">{order.order_number}</b> and your name <b className="text-white">{custName}</b>, and send as <b className="text-white">Friends &amp; Family</b> so we can match your payment.</>
                ) : (
                  <>In the Zelle memo add order <b className="text-white">{order.order_number}</b> and your name <b className="text-white">{custName}</b> so we can match your payment.</>
                )}
              </p>
            </div>
          )}
        </div>

        {/* Order summary */}
        <div className="border border-white/15 bg-[#0B1024] p-6 sq-lg" data-testid="order-summary">
          <div className="space-y-3">
            {order.items.map((it, i) => (
              <div key={i} className="flex justify-between text-sm">
                <span className="text-white/70 uppercase tracking-wide text-xs">{it.name} × {it.quantity}</span>
                <span className="text-white font-bold">{money(it.price * it.quantity)}</span>
              </div>
            ))}
          </div>
          <div className="mt-5 pt-4 border-t border-white/12 space-y-2 text-sm">
            <div className="flex justify-between text-white/60"><span className="text-xs uppercase tracking-wide">Subtotal</span><span className="text-white">{money(order.subtotal)}</span></div>
            {order.discount_amount > 0 && (
              <div className="flex justify-between text-electric-400"><span className="text-xs uppercase tracking-wide">Discount ({order.discount_code})</span><span>minus {money(order.discount_amount)}</span></div>
            )}
            <div className="flex justify-between text-white/60"><span className="text-xs uppercase tracking-wide">Shipping ({order.shipping_label})</span><span className="text-white">{order.shipping_cost ? money(order.shipping_cost) : "Free"}</span></div>
            <div className="flex justify-between items-baseline pt-3 border-t border-white/12">
              <span className="text-xs tracking-[0.2em] uppercase font-bold text-white">Total</span>
              <span className="font-display font-bold text-2xl tracking-tighter text-white">{money(order.total)}</span>
            </div>
          </div>
          <div className="mt-5 pt-4 border-t border-white/12 flex items-start gap-2 text-[11px] text-white/50">
            <Truck size={14} className="text-electric mt-0.5 shrink-0" />
            <span>Ships to {order.shipping?.first_name} {order.shipping?.last_name}, {order.shipping?.city}, {order.shipping?.state} {order.shipping?.zip} once payment clears.</span>
          </div>
        </div>

        {order.subscription?.active && (
          <div className="border border-white/15 bg-[#0B1024] p-6 mt-6 sq-lg" data-testid="subscription-panel">
            <div className="flex items-center gap-2 mb-2">
              <RefreshCw size={16} className="text-electric" />
              <span className="text-[10px] tracking-[0.25em] uppercase font-bold text-electric">Monthly Subscription Active</span>
            </div>
            <p className="text-sm text-white/60 leading-relaxed">
              We'll email a reorder reminder around <b className="text-white">{String(order.subscription.next_reorder_at).slice(0, 10)}</b>. Your first month is 15% off with CENTURY. It's a manual subscription, no automatic charge, and you can cancel anytime from the reminder email.
            </p>
          </div>
        )}

        <Link to="/" className="btn-primary block text-center mt-8 py-4 text-xs tracking-[0.2em] uppercase font-bold" data-testid="confirmation-continue">
          Continue Shopping
        </Link>
      </div>
    </div>
  );
}
